import { store } from '@/app/store';
import Config from '@/config';
import { getMessageType } from '@/features/chats/sub-features/media/utils/mediaUtils';
import { isEnabled, SERVER_FEATURE_MAP } from '@/features/chats/chatsRegistry';
import {
   ALLOWED_ALL_FILE_FORMATS,
   ALLOWED_AUDIO_FORMATS,
   ALLOWED_DOCUMENT_FORMATS,
   ALLOWED_IMAGE_FORMATS,
   ALLOWED_IMAGE_VIDEO_FORMATS,
   ALLOWED_VIDEO_FORMATS,
   AUDIO_FORMATS,
   DOCUMENT_FORMATS,
   FEATURE_RESTRICTION_ERROR_MESSAGE,
   IMAGE_FORMATS,
   IMAGE_VIDEO_FORMATS,
   VIDEO_FORMATS,
} from '@/shared/constants/mediaConstants';
import { formatBytes, getExtension } from '@/shared/utils/mediaUtils';
import { toast } from 'react-toastify';

const { maxAllowedMediaCount, fileSize, imageFileSize, videoFileSize, audioFileSize, documentFileSize } = Config;

const MAX_SIZE_BY_TYPE = {
   image: imageFileSize,
   video: videoFileSize,
   audio: audioFileSize,
   file: documentFileSize,
};

const FORMATS_BY_MEDIA_TYPE = {
   imagevideo: ALLOWED_IMAGE_VIDEO_FORMATS,
   image: ALLOWED_IMAGE_FORMATS,
   video: ALLOWED_VIDEO_FORMATS,
   audio: ALLOWED_AUDIO_FORMATS,
   file: ALLOWED_DOCUMENT_FORMATS,
};

const MIME_FORMAT_CHECKS = [
   {
      mimeList: IMAGE_VIDEO_FORMATS,
      allowedFormats: ALLOWED_IMAGE_VIDEO_FORMATS,
      flags: ['isImageAttachmentEnabled', 'isVideoAttachmentEnabled'],
   },
   { mimeList: IMAGE_FORMATS, allowedFormats: ALLOWED_IMAGE_FORMATS, flags: ['isImageAttachmentEnabled'] },
   { mimeList: VIDEO_FORMATS, allowedFormats: ALLOWED_VIDEO_FORMATS, flags: ['isVideoAttachmentEnabled'] },
   { mimeList: AUDIO_FORMATS, allowedFormats: ALLOWED_AUDIO_FORMATS, flags: ['isAudioAttachmentEnabled'] },
   { mimeList: DOCUMENT_FORMATS, allowedFormats: ALLOWED_DOCUMENT_FORMATS, flags: ['isDocumentAttachmentEnabled'] },
];

const showError = message => {
   toast.dismiss();
   toast.error(message);
};

const isValidFormat = (fileExtension, allowedFormats) =>
   new RegExp(`([a-zA-Z0-9s_\\.-:])+(${allowedFormats.join('|')})$`, 'i').test(fileExtension);

const getFeatureFlags = () => {
   const serverState = store.getState().features || {};
   return ['photo', 'video', 'audio', 'document'].reduce((acc, name) => {
      const serverKey = SERVER_FEATURE_MAP[name];
      acc[serverKey] = isEnabled(name) && (serverState[serverKey] ?? true);
      return acc;
   }, {});
};

const validateFileSize = file => {
   const mediaType = getMessageType(file.type, file);
   const maxSizeKB = (MAX_SIZE_BY_TYPE[mediaType] ?? fileSize) * 1024;
   const fileSizeKB = Math.round(file.size / 1024);

   if (fileSizeKB >= maxSizeKB) {
      showError(`File size is too large. Try uploading file size below ${formatBytes(maxSizeKB * 1024)}`);
      return false;
   }
   return true;
};

/**
 * Validates file extension when a specific mediaType is provided by the caller.
 */
const validateByMediaType = (fileExtension, mediaType) => {
   const allowedFormats = FORMATS_BY_MEDIA_TYPE[mediaType];
   if (!allowedFormats) return true; // Unknown mediaType — skip format check

   if (!isValidFormat(fileExtension, allowedFormats)) {
      showError(`Unsupported file format. Files allowed: ${allowedFormats.join(', ')}`);
      return false;
   }
   return true;
};

const validateByAutoDetect = (fileExtension, fileMimeType, featureFlags) => {
   const enabledFlags = Object.values(featureFlags);
   const allEnabled = enabledFlags.every(Boolean);
   const anyEnabled = enabledFlags.some(Boolean);
   const unknownMime = !fileMimeType;

   if (allEnabled || (unknownMime && anyEnabled)) {
      if (!isValidFormat(fileExtension, ALLOWED_ALL_FILE_FORMATS)) {
         showError(`Unsupported file format. Files allowed: ${ALLOWED_ALL_FILE_FORMATS.join(', ')}`);
         return false;
      }
      return true;
   }

   const matched = MIME_FORMAT_CHECKS.find(
      ({ mimeList, flags }) => mimeList.includes(fileMimeType) && flags.every(flag => featureFlags[flag]),
   );

   if (matched) {
      if (!isValidFormat(fileExtension, matched.allowedFormats)) {
         showError(`Unsupported file format. Files allowed: ${matched.allowedFormats.join(', ')}`);
         return false;
      }
      return true;
   }

   showError(FEATURE_RESTRICTION_ERROR_MESSAGE);
   return false;
};

export const validateFileExtension = async (file, mediaType) => {
   const fileExtension = getExtension(file.name);
   const featureFlags = getFeatureFlags();

   const isFormatValid = mediaType
      ? validateByMediaType(fileExtension, mediaType)
      : validateByAutoDetect(fileExtension, file.type, featureFlags);

   return isFormatValid && validateFileSize(file);
};

export const sendErrorMessage = () => {
   showError(`Can't share more than ${maxAllowedMediaCount} media items.`);
};

export const validateFile = async (selectedFiles, mediaType, currentCount = 0) => {
   const remainingSlots = maxAllowedMediaCount - currentCount;

   if (remainingSlots <= 0) {
      sendErrorMessage();
      return [];
   }

   const filesToProcess = selectedFiles.slice(0, remainingSlots);

   if (selectedFiles.length > remainingSlots) {
      showError(`Only ${remainingSlots} file(s) can be added. Extra files will be ignored.`);
   }

   const validationResults = await Promise.all(filesToProcess.map(file => validateFileExtension(file, mediaType)));

   return filesToProcess.filter((_, i) => validationResults[i]);
};
